import moment from 'moment/moment';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  DealActivityLinkType,
  DealActivityType,
  DealEventActivityViewModel,
  UserProfile,
} from '@trnsact/trnsact-shared-types';

import { CalendarEvent, CalendarDateRange } from '../ui/calendar';
import { CalendarView } from '../types';
import { CreateEventFormValues } from '../ui/dialogs/createEventFormSchema';
import { CalendarEventUpdateFormValues } from '../ui/card/calendarEventUpdateFormSchema';
import { CreateCallActivityVariables, UpdateCallActivityVariables } from '../../api/types';
import { ReDateEventsInput } from '../api';
import { DateFormats } from '../../../../constants/common';

interface EventDatesFormValues {
  allDay: boolean;
  endAt: string | null;
  endDate: string | null;
  startAt: string | null;
  startDate: string | null;
}

interface EventDatesPayload {
  allDay: boolean;
  endAt?: string;
  endDate?: string;
  startAt?: string;
  startDate?: string;
}

export class CalendarMapper {
  static mapFormDtoToCreate = (
    values: CreateEventFormValues,
    currentUserAccountId: string
  ): CreateCallActivityVariables['input'] => {
    if (!currentUserAccountId) throw new Error('User accountId is required to create a deal activity');

    const { accountId, assignedTo, allDay, startAt, startDate, endAt, endDate, ...rest } = values;

    return {
      type: DealActivityType.Event,
      parentId: currentUserAccountId,
      parentType: DealActivityLinkType.Account,
      event: {
        accountId,
        ...rest,
        ...CalendarMapper.buildEventDatesPayload({ allDay, startAt, startDate, endAt, endDate }),
      },
    };
  };

  static mapFormDtoToUpdate = (
    event: DealEventActivityViewModel,
    values: CalendarEventUpdateFormValues
  ): UpdateCallActivityVariables['input'] => {
    const { allDay, startAt, startDate, endAt, endDate, ...rest } = values;

    return {
      id: event.id,
      parentId: event.parentId,
      type: DealActivityType.Event,
      parentType: DealActivityLinkType.Account,
      event: {
        ...rest,
        ...CalendarMapper.buildEventDatesPayload({ allDay, startAt, startDate, endAt, endDate }),
      },
    };
  };

  static mapDtoToReDate = (dto: EventInteractionArgs<CalendarEvent>): ReDateEventsInput['input']['events'][number] => {
    if (dto.event.allDay) {
      return {
        allDay: true,
        id: dto.event.id,
        startDate: moment(dto.start).format(DateFormats.IsoDate),
        endDate: moment(dto.end).subtract(1, 'day').format(DateFormats.IsoDate),
      };
    }

    return {
      allDay: false,
      id: dto.event.id,
      endAt: moment(dto.end).toISOString(),
      startAt: moment(dto.start).toISOString(),
    };
  };

  static getHiddenCount = (anchor: HTMLElement, total: number): number => {
    const parsed = parseInt((anchor.textContent ?? '').replace(/\D/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : total;
  };

  static getVisibleRange = (view: CalendarView, date: Date): CalendarDateRange => {
    const current = moment(date);

    switch (view) {
      case CalendarView.Day:
        return {
          from: current.clone().startOf('day').toISOString(),
          to: current.clone().endOf('day').toISOString(),
        };
      case CalendarView.Week:
        return {
          from: current.clone().startOf('week').toISOString(),
          to: current.clone().endOf('week').toISOString(),
        };
      case CalendarView.Month:
      default:
        return {
          from: current.clone().startOf('month').startOf('week').toISOString(),
          to: current.clone().endOf('month').endOf('week').toISOString(),
        };
    }
  };

  static getEventsSignature = (events: CalendarEvent[]): string =>
    events
      .map(event => {
        const start = event.start ? new Date(event.start).getTime() : 0;
        const end = event.end ? new Date(event.end).getTime() : 0;

        return `${event.id}:${start}:${end}:${event.allDay ? 1 : 0}`;
      })
      .join('|');

  static moveEventInList = (events: CalendarEvent[], dto: EventInteractionArgs<CalendarEvent>): CalendarEvent[] => {
    const start = new Date(dto.start);
    const end = new Date(dto.end);

    return events.map(event =>
      event.id === dto.event.id ? { ...event, start, end, durationMinutes: moment(end).diff(start, 'minutes') } : event
    );
  };

  private static buildEventDatesPayload = ({
    allDay,
    endAt,
    endDate,
    startAt,
    startDate,
  }: EventDatesFormValues): EventDatesPayload => {
    const payload: EventDatesPayload = { allDay };

    if (allDay) {
      // All-day dates must be timezone-independent: send a pure local date ("YYYY-MM-DD")
      // instead of a UTC instant, otherwise the @db.Date column truncates to the UTC date
      // and the day shifts back for positive offsets (e.g. UTC+3).
      if (startDate) payload.startDate = moment(startDate).format(DateFormats.IsoDate);
      if (endDate) payload.endDate = moment(endDate).format(DateFormats.IsoDate);
    } else {
      if (startAt) payload.startAt = startAt;
      if (endAt) payload.endAt = endAt;
    }

    return payload;
  };
}
